import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { refreshBranding, useBranding } from "../lib/branding";
import { useAuth } from "../contexts/AuthContext";
import LookupManager from "../components/LookupManager";
import {
  Save, Upload, Trash2, Lock, KeyRound, UserPlus, ShieldCheck, ShieldOff,
  Users as UsersIcon, User as UserIcon, Database, Plus, Bell, Tag, Table2,
  Image as ImageIcon, PauseCircle, PlayCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const CRM_FIELDS = [
  { value: "", label: "— ignore —" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "course_interest", label: "Course Interest" },
  { value: "education_level", label: "Education Level" },
  { value: "career_goal", label: "Career Goal" },
  { value: "budget", label: "Budget" },
  { value: "notes", label: "Notes" },
  { value: "summary", label: "Summary" },
  { value: "campaign_name", label: "Campaign Name" },
  { value: "adset_name", label: "Ad Set Name" },
  { value: "ad_name", label: "Ad Name" },
  { value: "keyword", label: "Keyword" },
  { value: "platform", label: "Platform" },
  { value: "source_channel", label: "Source Channel" },
  { value: "referred_by", label: "Referred By" },
  { value: "inbox_url", label: "Inbox URL" },
  { value: "is_organic", label: "Is Organic" },
  { value: "created_at", label: "Created At" },
  { value: "source_status", label: "Source Status" },
  { value: "lead_id", label: "Lead ID (external)" },
  { value: "form_name", label: "Form Name" },
  { value: "form_id", label: "Form ID" },
  { value: "ad_id", label: "Ad ID" },
  { value: "adset_id", label: "Ad Set ID" },
  { value: "campaign_id", label: "Campaign ID" },
];

function FieldComboBox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const selectedRef = useRef(false);

  const knownOpt = CRM_FIELDS.find((f) => f.value === value);
  const isCustom = value && value !== "" && !knownOpt;

  const filtered = query
    ? CRM_FIELDS.filter(
        (f) =>
          f.label.toLowerCase().includes(query.toLowerCase()) ||
          f.value.toLowerCase().includes(query.toLowerCase())
      )
    : CRM_FIELDS;

  const handleSelect = (opt) => {
    selectedRef.current = true;
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const handleFocus = () => {
    setQuery("");
    setOpen(true);
  };

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Update parent immediately so Save Mapping always captures the current text
    const match = CRM_FIELDS.find(
      (f) =>
        f.label.toLowerCase() === q.toLowerCase() ||
        f.value.toLowerCase() === q.toLowerCase()
    );
    onChange(q.trim() ? (match ? match.value : q) : "");
  };

  const handleBlur = () => {
    setTimeout(() => {
      selectedRef.current = false;
      setOpen(false);
      setQuery("");
    }, 200);
  };

  const displayText = knownOpt ? knownOpt.label : (value || "");

  // Compute portal position fresh from DOM on every render
  let portalStyle = { position: "fixed", zIndex: 9999, top: -9999, left: -9999, width: 200 };
  if (open && inputRef.current) {
    const rect = inputRef.current.getBoundingClientRect();
    portalStyle = { position: "fixed", zIndex: 9999, top: rect.bottom + 2, left: rect.left, width: rect.width };
  }

  const dropdown = open ? createPortal(
    <div style={portalStyle} className="bg-white border border-[#E8EAEF] rounded-[8px] shadow-lg max-h-48 overflow-y-auto">
      {filtered.map((f) => (
        <div
          key={f.value || "__ignore__"}
          onMouseDown={() => handleSelect(f)}
          className={`px-3 py-1.5 text-[12px] cursor-pointer hover:bg-[#F0F1F7] flex items-center justify-between ${f.value === value ? "bg-[#F0F1F7] font-[600]" : ""}`}
        >
          {f.value === "" ? (
            <span className="text-[#8B90A0] italic">{f.label}</span>
          ) : (
            <>
              <span>{f.label}</span>
              <span className="text-[10px] text-[#8B90A0] font-mono ml-2 shrink-0">{f.value}</span>
            </>
          )}
        </div>
      ))}
      {query.trim() && !filtered.some(
        (f) => f.value === query.trim() || f.label.toLowerCase() === query.trim().toLowerCase()
      ) && (
        <div
          onMouseDown={() => handleSelect({ value: query.trim(), label: query.trim() })}
          className="px-3 py-1.5 text-[12px] cursor-pointer hover:bg-purple-50 border-t border-[#E8EAEF] flex items-center gap-2"
        >
          <span className="text-purple-700 font-[600]">+ Use &ldquo;{query.trim()}&rdquo;</span>
          <span className="text-[10px] text-[#8B90A0]">custom field</span>
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : displayText}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="select or type a field…"
          className={`w-full px-2.5 py-1.5 border rounded-[6px] text-[12px] bg-white outline-none focus:border-[#6C47FF] ${isCustom ? "border-purple-300 pr-[52px]" : "border-[#E8EAEF]"}`}
        />
        {isCustom && !open && (
          <span className="absolute right-2 text-[10px] text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full font-[600] pointer-events-none">
            custom
          </span>
        )}
      </div>
      {dropdown}
    </div>
  );
}

export default function Settings() {
  const { user: me, setUser } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const branding = useBranding();
  const [uploading, setUploading] = useState(false);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const [users, setUsers] = useState([]);
  const [newU, setNewU] = useState({ name: "", email: "", password: "", role: "staff", mcube_agent_number: "" });
  const [creating, setCreating] = useState(false);
  const isAdmin = me?.role === "admin";
  useEffect(() => { if (me && !isAdmin) navigate("/", { replace: true }); }, [me, isAdmin, navigate]);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [resetPwSaving, setResetPwSaving] = useState(false);

  const [profile, setProfile] = useState({ name: me?.name || "", email: me?.email || "", mcube_agent_number: me?.mcube_agent_number || "" });
  const [profileSaving, setProfileSaving] = useState(false);

  const [mappingSource, setMappingSource] = useState("meta");
  const [mappingUrl, setMappingUrl] = useState("");
  const [mappingUrlOptions, setMappingUrlOptions] = useState([]);
  const mappingRowsRef = useRef([]);
  const [mappingRows, setMappingRows] = useState([]);
  const setMappingRowsSync = (rows) => {
    mappingRowsRef.current = rows;
    setMappingRows(rows);
  };
  const [mappingIsCustom, setMappingIsCustom] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);

  const [deduping, setDeduping] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [applyingRecovery, setApplyingRecovery] = useState(false);
  const [onlyStage, setOnlyStage] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [snapping, setSnapping] = useState(false);
  const [courseAudit, setCourseAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [normalizing, setNormalizing] = useState(false);

  const [activeSection, setActiveSection] = useState("profile");
  const [lookupTab, setLookupTab] = useState("category");

  const loadSheetMapping = async (src, url = "") => {
    setMappingSource(src);
    setMappingUrl(url);
    setMappingLoading(true);
    try {
      const [mappingRes, syncRes] = await Promise.all([
        api.get(`/settings/sheet-mapping?source=${src}${url ? `&url=${encodeURIComponent(url)}` : ""}`),
        api.get("/sync/status"),
      ]);
      const srcUrls = syncRes.data[`sheet_sync_${src}_urls`] || [];
      const filteredUrls = srcUrls.filter((u) => u.url.trim());
      setMappingUrlOptions(filteredUrls);

      // Auto-select the sheet URL when exactly one is configured and none was specified,
      // so the per-URL mapping (where the user configures fields) is shown by default.
      if (!url && filteredUrls.length === 1) {
        const autoUrl = filteredUrls[0].url;
        setMappingUrl(autoUrl);
        const autoRes = await api.get(`/settings/sheet-mapping?source=${src}&url=${encodeURIComponent(autoUrl)}`);
        setMappingRowsSync(Object.entries(autoRes.data.mapping).map(([col, field]) => ({ col, field })));
        setMappingIsCustom(autoRes.data.is_custom);
      } else {
        setMappingRowsSync(Object.entries(mappingRes.data.mapping).map(([col, field]) => ({ col, field })));
        setMappingIsCustom(mappingRes.data.is_custom);
      }
    } catch {
      toast.error("Failed to load mapping");
    } finally {
      setMappingLoading(false);
    }
  };

  const saveSheetMapping = async () => {
    const mapping = {};
    for (const { col, field } of mappingRowsRef.current) {
      if (col.trim()) mapping[col.trim()] = field;
    }
    setMappingSaving(true);
    try {
      await api.put("/settings/sheet-mapping", { source: mappingSource, url: mappingUrl || undefined, mapping });
      setMappingIsCustom(true);
      toast.success("Mapping saved — takes effect on next sync");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setMappingSaving(false);
    }
  };

  const resetSheetMapping = async () => {
    if (!window.confirm("Reset to default mapping? Your custom mapping will be deleted.")) return;
    try {
      const qs = mappingUrl ? `?source=${mappingSource}&url=${encodeURIComponent(mappingUrl)}` : `?source=${mappingSource}`;
      const { data } = await api.delete(`/settings/sheet-mapping${qs}`);
      setMappingRowsSync(Object.entries(data.mapping).map(([col, field]) => ({ col, field })));
      setMappingIsCustom(false);
      toast.success("Reset to defaults");
    } catch {
      toast.error("Reset failed");
    }
  };

  const addMappingRow = () => setMappingRowsSync([...mappingRowsRef.current, { col: "", field: "" }]);
  const removeMappingRow = (i) => setMappingRowsSync(mappingRowsRef.current.filter((_, idx) => idx !== i));
  const updateMappingRow = (i, key, val) =>
    setMappingRowsSync(mappingRowsRef.current.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const runCourseAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get("/admin/course-audit");
      setCourseAudit(data);
      const nonCanonical = (data.rows || []).filter((r) => !r.matches_canonical && r.canonical).length;
      if (nonCanonical === 0) toast.success("All course values are already canonical");
      else toast.info(`Found ${nonCanonical} non-canonical variant(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally { setAuditLoading(false); }
  };

  const runCourseNormalize = async () => {
    if (!window.confirm("Rewrite non-canonical course_interest values to their canonical form?")) return;
    setNormalizing(true);
    try {
      const { data } = await api.post("/admin/normalize-courses?dry_run=false");
      toast.success(`Updated ${data.updated} lead(s)`);
      runCourseAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Normalize failed");
    } finally { setNormalizing(false); }
  };

  const loadSnapshots = async () => {
    try {
      const { data } = await api.get("/admin/snapshots");
      setSnapshots(data.snapshots || []);
    } catch { /* silent */ }
  };

  const runDryRunRecovery = async () => {
    setRecovering(true);
    setRecovery(null);
    try {
      const url = `/admin/recover-stages?dry_run=true${onlyStage ? `&only_stage=${encodeURIComponent(onlyStage)}` : ""}`;
      const { data } = await api.post(url);
      setRecovery(data);
      if (data.would_change_count === 0) toast.info(`Scanned ${data.scanned} leads — nothing to restore`);
      else toast.success(`Found ${data.would_change_count} lead(s) with recoverable stages (dry-run)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recovery scan failed");
    } finally { setRecovering(false); }
  };

  const applyRecovery = async () => {
    if (!recovery || !recovery.would_change_count) return;
    if (!window.confirm(`Restore stage on ${recovery.would_change_count} lead(s)? Every restored lead will get a 'stage_restored' audit entry. This is reversible only by another manual change.`)) return;
    setApplyingRecovery(true);
    try {
      const url = `/admin/recover-stages?dry_run=false${onlyStage ? `&only_stage=${encodeURIComponent(onlyStage)}` : ""}`;
      const { data } = await api.post(url);
      toast.success(`Restored stage on ${data.applied} lead(s)`);
      setRecovery(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setApplyingRecovery(false); }
  };

  const takeSnapshotNow = async () => {
    setSnapping(true);
    try {
      const { data } = await api.post("/admin/snapshot-leads-now");
      if (data.skipped) toast.info(`Snapshot for ${data.date} already exists (${data.existing_docs} leads)`);
      else toast.success(`Snapshot taken: ${data.inserted} leads for ${data.date}`);
      loadSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    } finally { setSnapping(false); }
  };

  const runDedupe = async () => {
    if (!window.confirm("This will scan all leads and delete duplicates that share the same phone number (keeping the oldest record per phone). Continue?")) return;
    setDeduping(true);
    setDedupeResult(null);
    try {
      const { data } = await api.post("/admin/dedupe-leads");
      setDedupeResult(data);
      if (data.leads_removed > 0) toast.success(`Removed ${data.leads_removed} duplicate lead(s) across ${data.duplicate_groups} phone number(s)`);
      else toast.success("No duplicate leads found");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe failed");
    } finally { setDeduping(false); }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setS({ ...r.data, smtp_password: "" }));
    if (isAdmin) { loadUsers(); loadSnapshots(); loadSheetMapping("meta"); }
  }, [isAdmin]);

  useEffect(() => {
    setProfile({ name: me?.name || "", email: me?.email || "", mcube_agent_number: me?.mcube_agent_number || "" });
  }, [me]);

  const loadUsers = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      if (!payload.smtp_password) delete payload.smtp_password;
      delete payload.logo_data;
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    if (file.size > 1_500_000) return toast.error("Image must be under 1.5 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshBranding();
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    await api.delete("/settings/logo");
    await refreshBranding();
    toast.success("Logo removed");
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) return toast.error("Fill all password fields");
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change failed");
    } finally { setPwSaving(false); }
  };

  const createUser = async () => {
    if (!newU.name || !newU.email || !newU.password) return toast.error("Fill all fields");
    if (newU.password.length < 6) return toast.error("Password must be at least 6 characters");
    setCreating(true);
    try {
      await api.post("/users", newU);
      toast.success(`User ${newU.email} created`);
      setNewU({ name: "", email: "", password: "", role: "staff", mcube_agent_number: "" });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleAllocation = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { allocation_paused: !u.allocation_paused });
      toast.success(u.allocation_paused ? `Lead allocation resumed for ${u.name}` : `Lead allocation paused for ${u.name}`);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveMcubeNumber = async (u, mcube_agent_number) => {
    try {
      await api.patch(`/users/${u.id}`, { mcube_agent_number });
      toast.success(`MCube number updated for ${u.name}`);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitResetPassword = async () => {
    if (resetPwValue.length < 6) return toast.error("Password must be at least 6 characters");
    setResetPwSaving(true);
    try {
      await api.patch(`/users/${resetPwUser.id}/password`, { new_password: resetPwValue });
      toast.success(`Password reset for ${resetPwUser.name}`);
      setResetPwUser(null);
      setResetPwValue("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setResetPwSaving(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      loadUsers();
      toast.success("User deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveProfile = async () => {
    if (!profile.name.trim() || !profile.email.trim()) return toast.error("Name and email required");
    setProfileSaving(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: profile.name.trim(),
        email: profile.email.trim(),
        mcube_agent_number: profile.mcube_agent_number.trim(),
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setProfileSaving(false); }
  };

  if (!s) return <div className="text-[#6B7280] text-sm py-8">Loading…</div>;

  const navGroups = [
    {
      label: "Account",
      items: [
        { id: "profile", label: "Profile", icon: UserIcon },
        { id: "branding", label: "Branding", icon: ImageIcon },
      ],
    },
    isAdmin ? {
      label: "Organization",
      items: [{ id: "team", label: "Team", icon: UsersIcon }],
    } : null,
    {
      label: "Workspace",
      items: [{ id: "notifications", label: "Notifications", icon: Bell }],
    },
    isAdmin ? {
      label: "Data",
      items: [
        { id: "lookups", label: "Lookups", icon: Tag },
        { id: "sheet-mapping", label: "Sheet Mapping", icon: Table2 },
        { id: "maintenance", label: "Maintenance", icon: Database },
      ],
    } : null,
  ].filter(Boolean);

  return (
    <>
      <style>{`
        @keyframes sectionIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .settings-section { animation: sectionIn 0.18s ease forwards; }
      `}</style>

      <div className="flex gap-7">
        {/* Sidebar nav */}
        <aside className="w-[172px] shrink-0">
          <div className="sticky top-6">
            <div className="mb-5">
              <h1 className="text-[16px] font-[800] text-[#0D0B1F]">Settings</h1>
              <p className="text-[11px] text-[#8B90A0] mt-0.5">Configuration &amp; preferences</p>
            </div>
            <div className="space-y-4">
              {navGroups.map((group) => (
                <div key={group.label}>
                  <p className="text-[9.5px] font-[700] text-[#8B90A0] uppercase tracking-[0.14em] mb-1 px-2">{group.label}</p>
                  <nav className="space-y-0.5">
                    {group.items.map(({ id, label, icon: Icon }) => {
                      const active = activeSection === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setActiveSection(id)}
                          className={`w-full flex items-center gap-2 px-2 py-[7px] rounded-[7px] text-[12.5px] transition-colors text-left ${
                            active
                              ? "bg-[#6C47FF]/10 text-[#6C47FF] font-[650]"
                              : "text-[#6B7280] hover:bg-[#F0F1F7] hover:text-[#0D0B1F] font-[500]"
                          }`}
                        >
                          <Icon className="h-[14px] w-[14px] shrink-0" />
                          {label}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Divider */}
        <div className="w-px bg-[#E8EAEF] shrink-0" />

        {/* Main content */}
        <div className="flex-1 min-w-0 max-w-[680px]">

          {/* ── PROFILE ── */}
          {activeSection === "profile" && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Profile" description="Your personal details and login credentials." />

              {isAdmin && (
                <SettingsCard title="Personal Details">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Display Name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} testid="profile-name" />
                    <Field label="Email Address" type="email" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} testid="profile-email" />
                  </div>
                  <div>
                    <Field label="MCube Agent Number" value={profile.mcube_agent_number} onChange={(v) => setProfile({ ...profile, mcube_agent_number: v })} testid="profile-mcube-number" />
                    <p className="text-[11px] text-[#8B90A0] mt-1.5">Your registered number in MCube IVR — required to place calls via the Call button on a lead.</p>
                  </div>
                  <Btn onClick={saveProfile} disabled={profileSaving} testid="profile-save">
                    <UserIcon className="h-3.5 w-3.5" />
                    {profileSaving ? "Saving…" : "Update Profile"}
                  </Btn>
                </SettingsCard>
              )}

              <SettingsCard title="Change Password">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Current Password" type="password" value={pw.current_password} onChange={(v) => setPw((p) => ({ ...p, current_password: v }))} testid="pw-current" />
                  <Field label="New Password" type="password" value={pw.new_password} onChange={(v) => setPw((p) => ({ ...p, new_password: v }))} testid="pw-new" />
                  <Field label="Confirm New Password" type="password" value={pw.confirm} onChange={(v) => setPw((p) => ({ ...p, confirm: v }))} testid="pw-confirm" />
                </div>
                <Btn onClick={changePassword} disabled={pwSaving} testid="pw-save">
                  <Lock className="h-3.5 w-3.5" />
                  {pwSaving ? "Updating…" : "Update Password"}
                </Btn>
              </SettingsCard>
            </div>
          )}

          {/* ── BRANDING ── */}
          {activeSection === "branding" && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Branding" description="Customize the logo shown in the top nav and login screen." />
              <SettingsCard title="Logo">
                <div className="flex items-start gap-6">
                  <div className="h-20 w-48 border border-[#E8EAEF] rounded-[10px] bg-[#F8F9FD] flex items-center justify-center overflow-hidden shrink-0" data-testid="logo-preview">
                    {branding.logo_data
                      ? <img src={branding.logo_data} alt="logo" className="max-h-full max-w-full object-contain" />
                      : <span className="text-xs text-[#C4C9DA]">No logo</span>}
                  </div>
                  <div className="space-y-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] btn-mx text-[12.5px] font-[600] min-h-[38px] cursor-pointer" data-testid="logo-upload-label">
                      <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Logo"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-upload-input" />
                    </label>
                    {branding.logo_data && (
                      <button onClick={removeLogo} data-testid="logo-remove-btn" className="flex items-center gap-2 px-4 py-2 rounded-[8px] border border-red-200 text-red-700 text-[12.5px] font-[600] min-h-[38px] hover:bg-red-50 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" /> Remove Logo
                      </button>
                    )}
                    <p className="text-[11px] text-[#8B90A0] max-w-[260px] leading-relaxed">PNG, JPG or SVG · max 1.5 MB · recommended height ~80 px</p>
                  </div>
                </div>
              </SettingsCard>
            </div>
          )}

          {/* ── TEAM ── */}
          {activeSection === "team" && isAdmin && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Team" description="Manage who has access to this CRM and their roles." />

              <SettingsCard title="Add Team Member">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name" value={newU.name} onChange={(v) => setNewU((p) => ({ ...p, name: v }))} testid="newuser-name" />
                  <Field label="Email" type="email" value={newU.email} onChange={(v) => setNewU((p) => ({ ...p, email: v }))} testid="newuser-email" />
                  <Field label="Password" type="password" value={newU.password} onChange={(v) => setNewU((p) => ({ ...p, password: v }))} testid="newuser-password" />
                  <div>
                    <label className="text-[11px] font-[700] text-[#8B90A0] uppercase tracking-[0.08em]">Role</label>
                    <select
                      value={newU.role}
                      onChange={(e) => setNewU((p) => ({ ...p, role: e.target.value }))}
                      data-testid="newuser-role"
                      className="w-full mt-1.5 rounded-[8px] border-[1.5px] border-[#E8EAEF] px-3 py-2 text-[13px] bg-[#FAFBFF] min-h-[40px] outline-none focus:border-[#6C47FF]"
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Field label="MCube Agent Number (optional)" value={newU.mcube_agent_number} onChange={(v) => setNewU((p) => ({ ...p, mcube_agent_number: v }))} testid="newuser-mcube-number" />
                  </div>
                </div>
                <Btn onClick={createUser} disabled={creating} testid="newuser-submit">
                  <UserPlus className="h-3.5 w-3.5" />
                  {creating ? "Creating…" : "Create User"}
                </Btn>
              </SettingsCard>

              <SettingsCard title="Members">
                <div className="space-y-2" data-testid="team-table">
                  {users.map((u) => {
                    const initials = (u.name || u.email || "?")[0].toUpperCase();
                    const isMe = u.id === me.id;
                    return (
                      <div
                        key={u.id}
                        data-testid={`user-row-${u.id}`}
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3.5 rounded-[10px] border border-[#F0F1F7] bg-[#FAFBFF] hover:border-[#E8EAEF] hover:bg-white transition-colors"
                      >
                        {/* Avatar */}
                        <div
                          className="h-9 w-9 rounded-full flex items-center justify-center text-white text-[13px] font-[800] shrink-0"
                          style={{ background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" }}
                        >
                          {initials}
                        </div>

                        {/* Name + email */}
                        <div className="min-w-[100px] flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-[650] text-[#0D0B1F] truncate">{u.name}</span>
                            {isMe && (
                              <span className="shrink-0 text-[9px] font-[600] text-[#8B90A0] border border-[#E8EAEF] px-1.5 py-0.5 rounded-full leading-none">you</span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#8B90A0] truncate mt-0.5">{u.email}</div>
                          {u.role === "staff" && u.allocation_paused && (
                            <span className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-[650] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              Leads paused
                            </span>
                          )}
                        </div>

                        {/* MCube number */}
                        <div className="shrink-0 hidden sm:block">
                          <div className="text-[9.5px] font-[700] text-[#8B90A0] uppercase tracking-[0.1em] mb-1">MCube #</div>
                          <McubeNumberCell user={u} onSave={saveMcubeNumber} />
                        </div>

                        {/* Role */}
                        <div className="shrink-0">
                          <select
                            value={u.role}
                            disabled={isMe}
                            onChange={(e) => changeRole(u, e.target.value)}
                            data-testid={`user-role-${u.id}`}
                            className="rounded-[7px] border border-[#E8EAEF] px-2.5 py-1.5 text-[12px] font-[600] bg-white disabled:opacity-50 outline-none focus:border-[#6C47FF] cursor-pointer"
                          >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>

                        {/* Status */}
                        <div className="shrink-0">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-[650] px-2.5 py-1 rounded-full ${
                            u.active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                          }`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${u.active ? "bg-green-500" : "bg-red-500"}`} />
                            {u.active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="shrink-0 ml-auto flex items-center gap-1.5">
                          <button
                            onClick={() => { setResetPwUser(u); setResetPwValue(""); }}
                            data-testid={`user-reset-password-${u.id}`}
                            title="Reset password"
                            className="h-8 w-8 rounded-[7px] border border-[#E8EAEF] text-[#6B7280] inline-flex items-center justify-center hover:bg-[#F0F1F7] hover:text-[#0D0B1F] transition-colors"
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                          </button>
                          {u.role === "staff" && (
                            <button
                              onClick={() => toggleAllocation(u)}
                              data-testid={`user-allocation-${u.id}`}
                              title={u.allocation_paused ? "Resume lead allocation" : "Pause lead allocation"}
                              className={`h-8 w-8 rounded-[7px] border inline-flex items-center justify-center transition-colors ${
                                u.allocation_paused
                                  ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "border-[#E8EAEF] text-[#6B7280] hover:bg-[#F0F1F7] hover:text-[#0D0B1F]"
                              }`}
                            >
                              {u.allocation_paused ? <PlayCircle className="h-3.5 w-3.5" /> : <PauseCircle className="h-3.5 w-3.5" />}
                            </button>
                          )}
                          <button
                            onClick={() => toggleActive(u)}
                            disabled={isMe}
                            data-testid={`user-toggle-${u.id}`}
                            title={u.active ? "Deactivate" : "Activate"}
                            className="h-8 w-8 rounded-[7px] border border-[#E8EAEF] text-[#6B7280] inline-flex items-center justify-center hover:bg-[#F0F1F7] hover:text-[#0D0B1F] disabled:opacity-40 transition-colors"
                          >
                            {u.active ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            onClick={() => deleteUser(u)}
                            disabled={isMe}
                            data-testid={`user-delete-${u.id}`}
                            title="Delete user"
                            className="h-8 w-8 rounded-[7px] border border-red-200 text-red-500 inline-flex items-center justify-center hover:bg-red-50 disabled:opacity-40 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#8B90A0] flex items-center gap-1.5 pt-1">
                  <UsersIcon className="h-3 w-3" /> Admins manage Team and Settings. Staff manage leads only.
                </p>
              </SettingsCard>
            </div>
          )}

          {/* ── NOTIFICATIONS ── */}
          {activeSection === "notifications" && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Notifications" description="Email delivery, SMTP configuration, and reminder behavior." />

              <SettingsCard title="Email Delivery">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Notify Email" value={s.notify_email} onChange={(v) => set("notify_email", v)} testid="settings-notify-email" />
                  <Field label="Daily Digest Hour (0–23 IST)" type="number" value={s.digest_hour} onChange={(v) => set("digest_hour", parseInt(v) || 9)} testid="settings-digest-hour" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SMTP Host" value={s.smtp_host} onChange={(v) => set("smtp_host", v)} testid="settings-smtp-host" />
                  <Field label="SMTP Port" type="number" value={s.smtp_port} onChange={(v) => set("smtp_port", parseInt(v) || 587)} testid="settings-smtp-port" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SMTP User" value={s.smtp_user} onChange={(v) => set("smtp_user", v)} testid="settings-smtp-user" />
                  <Field label="SMTP Password" type="password" placeholder="(unchanged)" value={s.smtp_password || ""} onChange={(v) => set("smtp_password", v)} testid="settings-smtp-password" />
                </div>
                <Field label="From Address" value={s.smtp_from} onChange={(v) => set("smtp_from", v)} testid="settings-smtp-from" />
                <p className="text-[11px] text-[#8B90A0]">Works with any standard SMTP provider (Resend, SendGrid, Gmail, etc.). Emails go only to the Notify Email — never to leads directly.</p>
              </SettingsCard>

              <SettingsCard title="Reminder Behavior">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!s.smart_defaults}
                    onChange={(e) => set("smart_defaults", e.target.checked)}
                    data-testid="settings-smart-defaults"
                    className="mt-0.5 h-4 w-4 rounded accent-[#6C47FF]"
                  />
                  <div>
                    <div className="text-[13px] font-[600] text-[#0D0B1F]">Smart-default reminder suggestions</div>
                    <div className="text-[11px] text-[#8B90A0] mt-0.5 leading-relaxed">
                      Suggests a follow-up date based on stage: New Enquiry +1d · Contacted +2d · Followup +3d · Demo Scheduled +1d · Demo Done +2d · Fees Shared +2d
                    </div>
                  </div>
                </label>
              </SettingsCard>

              <div className="flex justify-end">
                <Btn onClick={save} disabled={saving} testid="settings-save">
                  <Save className="h-3.5 w-3.5" />
                  {saving ? "Saving…" : "Save Changes"}
                </Btn>
              </div>
            </div>
          )}

          {/* ── LOOKUPS ── */}
          {activeSection === "lookups" && isAdmin && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Lookups" description="Manage the values available in dropdowns across the CRM." />

              <div className="flex gap-1 p-1 bg-[#F0F1F7] rounded-[10px] w-fit">
                {[
                  { id: "category", label: "Lead Stages" },
                  { id: "stage", label: "Sub-Stages" },
                  { id: "course", label: "Courses" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setLookupTab(tab.id)}
                    className={`px-4 py-1.5 rounded-[8px] text-[12.5px] font-[600] transition-all ${
                      lookupTab === tab.id
                        ? "bg-white text-[#0D0B1F] shadow-sm"
                        : "text-[#6B7280] hover:text-[#0D0B1F]"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {lookupTab === "category" && (
                <SettingsCard title="Lead Stages" description="High-level lead stages (Untouched, Cold, Warm, etc.). Auto-set when sub-stage changes.">
                  <LookupManager kind="category" title="Lead Stages" description="High-level lead stages auto-set from sub-stage changes." withColor />
                </SettingsCard>
              )}
              {lookupTab === "stage" && (
                <SettingsCard title="Lead Sub-Stages" description="Kanban columns and sub-stage chips. Reorder to change Kanban column order. Changing a lead's sub-stage auto-updates its Lead Stage.">
                  <LookupManager kind="stage" title="Lead Sub-Stages" description="Kanban columns. Changing a sub-stage auto-updates the lead's Lead Stage." withColor />
                </SettingsCard>
              )}
              {lookupTab === "course" && (
                <SettingsCard title="Courses Offered" description="Course choices on lead forms and filters.">
                  <LookupManager kind="course" title="Courses Offered" description="Course choices on lead forms and filters." />
                </SettingsCard>
              )}
            </div>
          )}

          {/* ── SHEET MAPPING ── */}
          {activeSection === "sheet-mapping" && isAdmin && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Sheet Mapping" description="Map Google Sheet column headers to CRM fields for each lead source. Changes take effect on the next sync." />

              <SettingsCard>
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-[700] text-[#8B90A0] uppercase tracking-[0.08em] mb-2">Lead Source</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { key: "meta", label: "Meta (FB + IG)" },
                        { key: "google", label: "Google" },
                        { key: "offline", label: "Offline" },
                        { key: "whatsapp", label: "WhatsApp" },
                      ].map((src) => (
                        <button
                          key={src.key}
                          onClick={() => loadSheetMapping(src.key, "")}
                          className={`px-3 py-1.5 rounded-[7px] text-[12.5px] font-[600] border transition-colors ${
                            mappingSource === src.key
                              ? "btn-mx border-transparent"
                              : "border-[#E8EAEF] text-[#6B7280] bg-white hover:bg-[#F0F1F7]"
                          }`}
                        >
                          {src.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {mappingUrlOptions.length > 0 && (
                    <div>
                      <p className="text-[11px] font-[700] text-[#8B90A0] uppercase tracking-[0.08em] mb-2">Mapping Scope</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <select
                          value={mappingUrl}
                          onChange={(e) => loadSheetMapping(mappingSource, e.target.value)}
                          className="rounded-[8px] border-[1.5px] border-[#E8EAEF] px-3 py-2 text-[13px] bg-[#FAFBFF] flex-1 min-w-[240px] outline-none focus:border-[#6C47FF]"
                        >
                          <option value="">All sheets (source-level default)</option>
                          {mappingUrlOptions.map((u, i) => (
                            <option key={u.url} value={u.url}>
                              {u.label || `Sheet ${i + 1}`} — {u.url.length > 50 ? u.url.slice(0, 50) + "…" : u.url}
                            </option>
                          ))}
                        </select>
                        <span className="text-[11px] text-[#8B90A0]">
                          {mappingUrl ? "Per-sheet mapping overrides source default." : "Source-level mapping applies to all sheets unless overridden."}
                        </span>
                      </div>
                    </div>
                  )}

                  {mappingLoading ? (
                    <div className="text-[13px] text-[#8B90A0] py-8 text-center">Loading…</div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          {mappingIsCustom
                            ? <span className="text-[11px] font-[650] text-green-700 bg-green-50 border border-green-200 px-2.5 py-0.5 rounded-full">Custom mapping active</span>
                            : <span className="text-[11px] text-[#8B90A0] border border-[#E8EAEF] px-2.5 py-0.5 rounded-full">Using defaults</span>}
                        </div>
                        <button onClick={addMappingRow} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] border border-[#E8EAEF] text-[12px] text-[#6B7280] font-[600] hover:bg-[#F0F1F7] transition-colors">
                          <Plus className="h-3.5 w-3.5" /> Add Row
                        </button>
                      </div>

                      <div className="border border-[#E8EAEF] rounded-[10px] overflow-hidden">
                        <table className="w-full text-[13px]">
                          <thead className="bg-[#F8F9FD]">
                            <tr>
                              <th className="text-left px-4 py-2.5 text-[10px] font-[700] text-[#8B90A0] uppercase tracking-[0.1em] w-[46%]">Sheet Column Header</th>
                              <th className="text-left px-4 py-2.5 text-[10px] font-[700] text-[#8B90A0] uppercase tracking-[0.1em]">Maps to CRM Field</th>
                              <th className="px-4 py-2.5 w-10" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#F8F9FD]">
                            {mappingRows.map((row, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2">
                                  <input
                                    value={row.col}
                                    onChange={(e) => updateMappingRow(i, "col", e.target.value)}
                                    placeholder="e.g. phone_number"
                                    className="w-full px-2.5 py-1.5 border border-[#E8EAEF] rounded-[6px] text-[12px] font-mono bg-white outline-none focus:border-[#6C47FF]"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <FieldComboBox
                                    value={row.field}
                                    onChange={(v) => updateMappingRow(i, "field", v)}
                                  />
                                </td>
                                <td className="px-4 py-2 text-center">
                                  <button onClick={() => removeMappingRow(i)} className="text-red-400 hover:text-red-600 transition-colors inline-flex" title="Remove row">
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {mappingRows.length === 0 && (
                              <tr>
                                <td colSpan={3} className="px-4 py-8 text-center text-[13px] text-[#8B90A0]">
                                  No mappings configured. Add a row or reset to defaults.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={resetSheetMapping}
                          className="px-4 py-2 rounded-[8px] border border-[#E8EAEF] text-[12.5px] font-[600] text-[#6B7280] hover:bg-[#F0F1F7] transition-colors min-h-[38px]"
                        >
                          Reset to Defaults
                        </button>
                        <Btn onClick={saveSheetMapping} disabled={mappingSaving}>
                          <Save className="h-3.5 w-3.5" />
                          {mappingSaving ? "Saving…" : "Save Mapping"}
                        </Btn>
                      </div>
                    </>
                  )}
                </div>
              </SettingsCard>
            </div>
          )}

          {/* ── MAINTENANCE ── */}
          {activeSection === "maintenance" && isAdmin && (
            <div className="settings-section space-y-4">
              <SectionHeader title="Data Maintenance" description="Admin tools for data integrity. These operations affect live data — always review before applying." />

              {/* Dedupe */}
              <SettingsCard title="Deduplicate Leads" accent="red">
                <p className="text-[13px] text-[#5c5a58] leading-relaxed">
                  Scans all leads for duplicates sharing the same phone number. For each group, the <strong>oldest record</strong> is kept — preserving its category, stage, notes, and follow-ups — and newer copies are deleted.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={runDedupe}
                    disabled={deduping}
                    data-testid="dedupe-leads-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-red-700 text-white text-[12.5px] font-[600] min-h-[38px] disabled:opacity-60 hover:bg-red-800 transition-colors"
                  >
                    <Database className="h-3.5 w-3.5" /> {deduping ? "Scanning…" : "Run Deduplicate"}
                  </button>
                  {dedupeResult && (
                    <div data-testid="dedupe-result" className="flex items-center gap-4 px-4 py-2 bg-[#F8F9FD] border border-[#E8EAEF] rounded-[8px] text-[12.5px]">
                      <span><span className="text-[#8B90A0]">Groups:</span> <strong data-testid="dedupe-groups">{dedupeResult.duplicate_groups}</strong></span>
                      <span><span className="text-[#8B90A0]">Removed:</span> <strong className="text-red-700" data-testid="dedupe-removed">{dedupeResult.leads_removed}</strong></span>
                      <span><span className="text-[#8B90A0]">Kept:</span> <strong className="text-green-700" data-testid="dedupe-kept">{dedupeResult.leads_kept}</strong></span>
                      {dedupeResult.backfilled_phone_key > 0 && (
                        <span className="text-[11px] text-[#8B90A0]">Backfilled {dedupeResult.backfilled_phone_key} legacy record(s)</span>
                      )}
                    </div>
                  )}
                </div>
              </SettingsCard>

              {/* Stage recovery */}
              <SettingsCard title="Recover Stages from Activity History">
                <p className="text-[13px] text-[#5c5a58] leading-relaxed">
                  If a sync or import overwrote lead stages, this scans every lead's activity timeline for the most recent stage change and can restore it. <strong>Always run a dry-run first</strong> to review what will change.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    value={onlyStage}
                    onChange={(e) => setOnlyStage(e.target.value)}
                    placeholder='Filter by current stage (optional, e.g. "New Enquiry")'
                    className="flex-1 min-w-[220px] px-3 py-2 border border-[#E8EAEF] rounded-[8px] text-[13px] bg-[#FAFBFF] outline-none focus:border-[#6C47FF]"
                    data-testid="recovery-only-stage"
                  />
                  <button
                    onClick={runDryRunRecovery}
                    disabled={recovering}
                    data-testid="recovery-dry-run-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#1A1A2E] text-white text-[12.5px] font-[600] disabled:opacity-60 min-h-[38px] hover:opacity-80 transition-opacity"
                  >
                    {recovering ? "Scanning…" : "Run Dry-Run"}
                  </button>
                </div>
                {recovery && (
                  <div data-testid="recovery-result" className="border border-[#E8EAEF] rounded-[10px] bg-[#F8F9FD] p-4 space-y-3">
                    <div className="grid grid-cols-4 gap-2">
                      <Stat label="Scanned" value={recovery.scanned} />
                      <Stat label="Would restore" value={recovery.would_change_count} color="#166534" testid="recovery-would-change" />
                      <Stat label="No history" value={recovery.no_history_count} />
                      <Stat label="Invalid target" value={recovery.invalid_target_count} color="#7f1d1d" />
                    </div>
                    {recovery.would_change_count > 0 && (
                      <>
                        <div className="max-h-[200px] overflow-y-auto border border-[#E8EAEF] rounded-[8px] bg-white text-[12px]">
                          <table className="w-full">
                            <thead className="bg-[#F8F9FD] sticky top-0">
                              <tr>
                                {["Lead", "Current", "Restore to", "Source"].map((h) => (
                                  <th key={h} className="text-left px-3 py-2 text-[10px] font-[700] text-[#8B90A0] uppercase">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#F0F1F7]">
                              {recovery.would_change.map((r) => (
                                <tr key={r.lead_id}>
                                  <td className="px-3 py-2">{r.name}</td>
                                  <td className="px-3 py-2 text-red-700">{r.current_stage || "—"}</td>
                                  <td className="px-3 py-2 text-green-700 font-[600]">{r.recoverable_stage}</td>
                                  <td className="px-3 py-2 text-[#8B90A0]">{r.source}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button
                          onClick={applyRecovery}
                          disabled={applyingRecovery}
                          data-testid="recovery-apply-btn"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-green-700 text-white text-[12.5px] font-[600] disabled:opacity-60 hover:bg-green-800 transition-colors"
                        >
                          {applyingRecovery ? "Applying…" : `Apply Restore to ${recovery.would_change_count} lead(s)`}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </SettingsCard>

              {/* Course audit */}
              <SettingsCard title="Course Value Audit">
                <p className="text-[13px] text-[#5c5a58] leading-relaxed">
                  Shows every distinct <code className="text-[11px] bg-[#F0F1F7] px-1.5 py-0.5 rounded">course_interest</code> value stored on leads and whether it matches a canonical name from Lookups → Courses. Non-canonical variants can break the Board filter.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={runCourseAudit}
                    disabled={auditLoading}
                    data-testid="course-audit-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#E8EAEF] text-[12.5px] font-[600] disabled:opacity-60 hover:bg-[#F0F1F7] transition-colors min-h-[38px]"
                  >
                    {auditLoading ? "Loading…" : "Run Course Audit"}
                  </button>
                  {courseAudit && (courseAudit.rows || []).some((r) => !r.matches_canonical && r.canonical) && (
                    <button
                      onClick={runCourseNormalize}
                      disabled={normalizing}
                      data-testid="course-normalize-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-green-700 text-white text-[12.5px] font-[600] disabled:opacity-60 hover:bg-green-800 transition-colors min-h-[38px]"
                    >
                      {normalizing ? "Applying…" : "Normalize Non-Canonical Values"}
                    </button>
                  )}
                </div>
                {courseAudit && (
                  <div className="border border-[#E8EAEF] rounded-[10px] overflow-hidden" data-testid="course-audit-table">
                    <table className="w-full text-[13px]">
                      <thead className="bg-[#F8F9FD]">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-[10px] font-[700] text-[#8B90A0] uppercase">Stored Value</th>
                          <th className="text-left px-4 py-2.5 text-[10px] font-[700] text-[#8B90A0] uppercase">Canonical</th>
                          <th className="text-right px-4 py-2.5 text-[10px] font-[700] text-[#8B90A0] uppercase">Leads</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0F1F7]">
                        {(courseAudit.rows || []).map((r, i) => (
                          <tr key={i}>
                            <td className="px-4 py-2.5 font-mono text-[12px]">{r.stored_value || "—"}</td>
                            <td className="px-4 py-2.5">
                              {r.matches_canonical
                                ? <span className="text-green-700">✓ {r.canonical}</span>
                                : r.canonical
                                  ? <span className="text-amber-600">→ {r.canonical}</span>
                                  : <span className="text-[#8B90A0]">unknown</span>}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SettingsCard>

              {/* Snapshots */}
              <SettingsCard title="Nightly Snapshots">
                <p className="text-[13px] text-[#5c5a58] leading-relaxed">
                  A full snapshot of the leads collection is taken automatically at <strong>03:15 IST</strong> daily and kept for 30 days. You can also take one on demand.
                </p>
                <button
                  onClick={takeSnapshotNow}
                  disabled={snapping}
                  data-testid="snapshot-now-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#E8EAEF] text-[12.5px] font-[600] disabled:opacity-60 hover:bg-[#F0F1F7] transition-colors min-h-[38px]"
                >
                  {snapping ? "Snapshotting…" : "Take Snapshot Now"}
                </button>
                {snapshots.length > 0 && (
                  <div data-testid="snapshots-list">
                    <p className="text-[10px] font-[700] text-[#8B90A0] uppercase tracking-[0.12em] mb-2">Available Snapshots</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {snapshots.map((snap) => (
                        <div key={snap.date} className="flex items-center justify-between px-3 py-2 bg-[#F8F9FD] border border-[#E8EAEF] rounded-[8px]">
                          <span className="font-[600] text-[13px]">{snap.date}</span>
                          <span className="text-[11px] text-[#8B90A0]">{snap.leads} leads</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </SettingsCard>
            </div>
          )}
        </div>
      </div>

      {/* Reset password modal */}
      <Dialog open={!!resetPwUser} onOpenChange={(v) => !v && setResetPwUser(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetPwUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[12px] text-[#8B90A0]">Sets their password directly. No email is sent — share the new password with them yourself.</p>
            <Field label="New Password" type="password" value={resetPwValue} onChange={setResetPwValue} testid="reset-password-input" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setResetPwUser(null)} className="px-4 py-2 rounded-[8px] border border-[#E8EAEF] text-[13px] font-[600] min-h-[40px] hover:bg-[#F0F1F7] transition-colors">
                Cancel
              </button>
              <Btn onClick={submitResetPassword} disabled={resetPwSaving} testid="reset-password-submit-admin">
                {resetPwSaving ? "Saving…" : "Reset Password"}
              </Btn>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────

function SectionHeader({ title, description }) {
  return (
    <div className="pb-4 border-b border-[#E8EAEF]">
      <h2 className="text-[18px] font-[800] text-[#0D0B1F] leading-tight">{title}</h2>
      {description && <p className="text-[13px] text-[#8B90A0] mt-1 leading-relaxed">{description}</p>}
    </div>
  );
}

function SettingsCard({ title, description, children }) {
  return (
    <div className="card p-6 space-y-4">
      {title && (
        <div className="pb-3 border-b border-[#F0F1F7]">
          <div className="text-[13px] font-[700] text-[#0D0B1F]">{title}</div>
          {description && <p className="text-[11px] text-[#8B90A0] mt-0.5">{description}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function Btn({ children, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="btn-mx rounded-[8px] px-4 py-2 text-[12.5px] font-[600] min-h-[38px] inline-flex items-center gap-2 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="bg-white rounded-[8px] border border-[#E8EAEF] px-3 py-2.5">
      <div className="text-[9px] text-[#8B90A0] uppercase tracking-[0.12em] font-[700]">{label}</div>
      <div className="text-[20px] font-[800] tabular-nums mt-0.5" style={{ color: color || "#0D0B1F" }} data-testid={testid}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-[700] text-[#8B90A0] uppercase tracking-[0.08em]">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="field-input mt-1.5"
      />
    </div>
  );
}

function McubeNumberCell({ user, onSave }) {
  const [value, setValue] = useState(user.mcube_agent_number || "");
  useEffect(() => { setValue(user.mcube_agent_number || ""); }, [user.mcube_agent_number]);
  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => { if (value !== (user.mcube_agent_number || "")) onSave(user, value.trim()); }}
      placeholder="Not set"
      data-testid={`user-mcube-number-${user.id}`}
      className="rounded-[6px] border border-[#E8EAEF] px-2 py-1 text-[12px] bg-white w-[110px] outline-none focus:border-[#6C47FF]"
    />
  );
}
